import { withSortsAndFilters } from 'common/api/utils/apiFunctions';
import { SavedFilterOptions } from 'common/hooks';
import { Grower, PaginatedResult, PaginationQueryParams } from 'common/models';
import { cmsApi } from '../cmsApi';
import { GrowerWithBlocks, GrowerWithoutBlocks } from '../dto/get-grower.dto';
import { GrowerUpdateRequest } from '../dto/put-grower.dto';
import { shouldForceRefetch } from '../utils/handleExpiredSession';

type GrowerFilterQueryParams = {
  sorts: []; // No sorting for now.
  filters: SavedFilterOptions[];
};

const growersBaseUrl = '/growers';

/**
 * Contains functions that define how cache IDs for grower-related endpoints are
 * formatted.
 */
export const cacheIdFormatters = {
  grower: (seasonId: number, growerId: number) => `/${seasonId}/${growerId}`,
  growers: (seasonId: number) => seasonId,
};

const withGrowerEndpoints = cmsApi.injectEndpoints({
  endpoints: builder => ({
    getGrowers: builder.query<
      PaginatedResult<GrowerWithBlocks>,
      PaginationQueryParams & GrowerFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, ...prm }) =>
        withSortsAndFilters(prm, `${growersBaseUrl}/list/${seasonId}`),
      providesTags: (_, err, { seasonId }) =>
        err
          ? []
          : [{ type: 'Growers', id: cacheIdFormatters.growers(seasonId) }],
      forceRefetch: shouldForceRefetch,
    }),

    getGrowerDataBySeason: builder.query<
      GrowerWithoutBlocks[],
      { seasonId: number }
    >({
      query: ({ seasonId }) => ({
        url: `${growersBaseUrl}/grower-data/${seasonId}`,
        method: 'GET',
      }),
      // Since this endpoint is for CSV export, we always want the latest data.
      forceRefetch: () => true,
    }),

    getGrowerById: builder.query<
      GrowerWithBlocks,
      { seasonId: number; growerId: number }
    >({
      query: ({ seasonId, growerId }) =>
        `${growersBaseUrl}/${seasonId}/${growerId}`,
      providesTags: (_, err, { seasonId, growerId }) =>
        err
          ? []
          : [
              'Grower',
              {
                type: 'Grower',
                id: cacheIdFormatters.grower(seasonId, growerId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    updateGrower: builder.mutation<
      Grower,
      GrowerUpdateRequest & { seasonId: number; growerId: number }
    >({
      query: ({ seasonId, growerId, ...payload }) => ({
        url: `${growersBaseUrl}/${seasonId}/${growerId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, err, { seasonId, growerId }) =>
        err
          ? []
          : [
              'Growers',
              {
                type: 'Grower',
                id: cacheIdFormatters.grower(seasonId, growerId),
              },
            ],
    }),
  }),
});

export const {
  useGetGrowersQuery,
  useGetGrowerDataBySeasonQuery,
  useGetGrowerByIdQuery,
  useUpdateGrowerMutation,
} = withGrowerEndpoints;
